import type { PairRoleSummary } from "../api/types";

/**
 * Разбор сводки теневой покупки пары дешевле доллара.
 *
 * Сервер шлёт числители со знаменателями и деньги строками; доли считаются тут,
 * чтобы знаменатель всегда стоял рядом с долей.
 */

/** Деньги из строки контракта. Пустая строка и мусор дают null, а не ноль. */
export function amount(raw: string | undefined | null): number | null {
  if (raw === undefined || raw === null || raw === "") return null;
  const v = Number(raw);
  return Number.isFinite(v) ? v : null;
}

/** Доля с прочерком вместо деления на ноль. */
export function rate(n: number, total: number): number | null {
  if (total <= 0) return null;
  return n / total;
}

/** Подпись роли. */
export function roleLabel(role: PairRoleSummary["role"]): string {
  return role === "taker" ? "обе ноги по рынку" : "нога мейкером, вторая тейкером";
}

/**
 * Комиссия пары на доллар выплаты при данной паре цен.
 *
 * `0.07 · p · (1 − p)` на каждой ноге. Функция здесь, а не на сервере, ровно
 * потому, что она ОБЪЯСНЯЕТ число, а не считает его: строка уже несёт
 * фактическую комиссию, а панели нужно показать, сколько стоила бы пара у
 * середины цены — там, где арбитраж выглядит самым заманчивым и не существует.
 */
export function feeOfPair(priceA: number, priceB: number): number {
  const leg = (p: number) => 0.07 * p * (1 - p);
  return leg(priceA) + leg(priceB);
}

/** Разобранная роль: всё, что панель показывает по одной строке. */
export interface PairView {
  row: PairRoleSummary;
  /** Доля наблюдений, в которых пара действительно собралась. */
  feasibleShare: number | null;
  /** Кромка на share и стоимость пары с комиссией. */
  edge: number | null;
  costNet: number | null;
  shares: number | null;
  /** Доля наливов от наблюдённых заявок; только у гибрида. */
  fillShare: number | null;
  /** Снос — цена голой ноги. Только у гибрида. */
  slip: number | null;
  nakedPnL: number | null;
  /**
   * Съедает ли снос кромку целиком. null означает «сравнивать не с чем».
   *
   * ГЛАВНЫЙ вопрос к гибриду: мейкера наливают ровно тогда, когда рынок пошёл
   * против него, и если снос больше кромки — роль закрывается этим числом и
   * больше ничем.
   */
  slipEatsEdge: boolean | null;
}

export function view(row: PairRoleSummary): PairView {
  const edge = amount(row.edge_median);
  const slip = row.slip_n > 0 ? amount(row.slip_median) : null;
  return {
    row,
    feasibleShare: rate(row.feasible, row.signals),
    edge,
    costNet: amount(row.cost_net_median),
    shares: amount(row.shares),
    fillShare: row.watched > 0 ? rate(row.filled, row.watched) : null,
    slip,
    nakedPnL: row.naked_priced > 0 ? amount(row.naked_pnl) : null,
    slipEatsEdge: edge !== null && slip !== null ? slip >= edge : null,
  };
}

/**
 * Хватает ли строк, чтобы читать медианы.
 *
 * Двадцать — то же число и тот же довод, что у соседних панелей. У пары порог
 * ещё существеннее: событие редкое, и медиана по трём строкам выглядит на экране
 * так же уверенно, как по тремстам.
 */
export const MIN_SIGNALS = 20;

export function trustworthy(row: PairRoleSummary): boolean {
  return row.signals >= MIN_SIGNALS;
}

/**
 * Вердикт по сводке одной фразой.
 *
 * Называет ровно то, что решает судьбу затеи, и в порядке убывания цены:
 * собралась ли пара вообще, съедает ли снос кромку, и только потом сама кромка.
 */
export function verdict(rows: PairRoleSummary[]): string {
  if (rows.length === 0) return "пар дешевле доллара не было";
  const parts: string[] = [];
  for (const row of rows) {
    const v = view(row);
    if (!trustworthy(row)) {
      parts.push(`${row.role}: ${row.signals} набл. — судить рано`);
      continue;
    }
    if (row.feasible === 0) {
      parts.push(`${row.role}: ни одна не собралась на ${row.signals} набл.`);
      continue;
    }
    if (v.slipEatsEdge === true) {
      parts.push(`${row.role}: снос съедает кромку`);
      continue;
    }
    parts.push(
      `${row.role}: кромка ${fmt(v.edge)} на ${row.feasible} из ${row.signals}`,
    );
  }
  return parts.join(" · ");
}

function fmt(v: number | null): string {
  return v === null ? "—" : v.toFixed(4);
}
